/**
 * BriefingCreatorCard — shield-shaped, flippable. The Act 1 redesign
 * strips ALL data off the FRONT face — the brand reads the cohort
 * story in the hero above the grid, then looks at faces below. Data
 * lives on the BACK face revealed by clicking the card.
 *
 * FRONT:
 *   • Face (fills 70% of the shield)
 *   • Name + handle in the tapering bottom
 *   • Nothing else. No badges. No metrics. No match scores.
 *
 * BACK (rotateY 180° on click):
 *   • +XX.XK NEW REACH   — cohort contribution
 *   • ● overlap risk     — green/amber/red
 *   • XX% MATCH          — DNA alignment
 *   • Followers · engagement · platform · niches
 *   • Dimensions matched count
 *   • View full profile link
 *
 * `outcome` prop (spotlight only):
 *   'matched'  — pink rim + pulse, slides into the holding zone
 *   'rejected' — red rim flash, dissolves
 *
 * `dim` fades the card to ~18% (holding zone, pre-arrival).
 */
import React, { useState } from 'react';
import { formatReach } from '../../lib/format';
import { ExternalLink, GitMerge, Sparkles } from 'lucide-react';

const TEAL        = '#00D4FF';
const PINK        = '#FF4D94';
const RED_REJECT  = '#EF4444';
const GREEN_LOW   = '#22D3A1';
const AMBER_MED   = '#F59E0B';
const RED_HIGH    = '#EF4444';

// The shield silhouette — 7-point heraldic polygon. Square shoulders,
// soft inward curves, pointed bottom. Same polygon for both faces so
// the flip feels continuous.
const SHIELD_CLIP = 'polygon(0 0, 100% 0, 100% 55%, 88% 80%, 50% 100%, 12% 80%, 0 55%)';

function dicebearFor(seed) {
  const s = encodeURIComponent((seed || 'x').replace(/[^a-zA-Z0-9]/g, ''));
  return `https://api.dicebear.com/7.x/lorelei/svg?seed=${s}&backgroundColor=0a0a0a,0f0f0f,121212&radius=12`;
}

function overlapColor(tag) {
  if (tag === 'high')   return RED_HIGH;
  if (tag === 'medium') return AMBER_MED;
  return GREEN_LOW;
}

export default function BriefingCreatorCard({
  creator,
  spotlight = false,
  outcome   = 'matched',
  dim       = false,
  // 'full' = name + handle, 'name-only' = name only, 'hidden' = neither.
  // Used by the radial network to suppress label collisions on dense
  // rings — top-priority shields keep full labels, lower-priority ones
  // yield to neighbours.
  nameMode  = 'full',
}) {
  const [flipped, setFlipped] = useState(false);

  if (!creator) return null;
  const seed     = creator.handle || creator.name || 'x';
  const avatar   = creator.avatar || dicebearFor(seed);
  const platform = (creator.platforms && creator.platforms[0]) || '';
  const matchPct = Math.round(creator.audience_match_pct ?? creator.dna_score ?? 0);
  const uniqueR  = creator.unique_reach_followers || 0;
  const ovrTag   = creator.overlap_risk_tag || 'low';
  const ovrColor = overlapColor(ovrTag);

  // ── Spotlight variant ──────────────────────────────────────────
  if (spotlight) {
    const isReject = outcome === 'rejected';
    const rimColor = isReject ? RED_REJECT : PINK;
    return (
      <div
        data-testid={`briefing-spotlight-${creator.handle}`}
        data-spotlight="true"
        data-outcome={outcome}
        style={{
          width:     180,
          height:    240,
          animation: isReject
            ? 'briefingSpotlightReject 900ms ease-out both'
            : 'briefingSpotlightMatch  900ms ease-out both',
          filter:    `drop-shadow(0 0 24px ${rimColor}88)`,
        }}
      >
        <ShieldFrontFace
          avatar={avatar}
          seed={seed}
          name={creator.name || creator.handle}
          handle={creator.handle}
          rimColor={rimColor}
          platform={platform}
          spotlight
          outcomeLabel={isReject ? 'REJECTED' : 'MATCHED'}
        />
      </div>
    );
  }

  // ── Compact card (holding zone) — flippable ────────────────────
  return (
    <div
      data-testid={`briefing-card-${creator.handle}`}
      data-dim={dim ? 'true' : 'false'}
      data-flipped={flipped ? 'true' : 'false'}
      className="relative cursor-pointer"
      style={{
        // 1:1.4 aspect — proper heraldic shield proportions
        height:       170,
        perspective:  '900px',
        opacity:      dim ? 0.18 : 1,
        transition:   'opacity 280ms ease-out',
      }}
      onClick={() => !dim && setFlipped(!flipped)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !dim) setFlipped(!flipped); }}
    >
      <div
        className="relative w-full h-full transition-transform duration-500"
        style={{
          transformStyle: 'preserve-3d',
          transform:      flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* FRONT — face + name + handle only */}
        <div
          className="absolute inset-0"
          style={{
            backfaceVisibility: 'hidden',
            animation:          dim ? 'none' : 'briefingCardRise 360ms ease-out both',
          }}
        >
          <ShieldFrontFace
            avatar={avatar}
            seed={seed}
            name={creator.name || creator.handle}
            handle={creator.handle}
            rimColor={TEAL}
            nameMode={nameMode}
          />
        </div>

        {/* BACK — full individual context */}
        <div
          className="absolute inset-0"
          style={{
            backfaceVisibility: 'hidden',
            transform:          'rotateY(180deg)',
          }}
        >
          <ShieldBackFace
            creator={creator}
            matchPct={matchPct}
            uniqueR={uniqueR}
            ovrTag={ovrTag}
            ovrColor={ovrColor}
            platform={platform}
          />
        </div>
      </div>
    </div>
  );
}


// ─── FRONT — face only ─────────────────────────────────────────
function ShieldFrontFace({
  avatar, seed, name, handle, rimColor,
  platform, spotlight = false, outcomeLabel,
  nameMode = 'full',
}) {
  const showName   = nameMode !== 'hidden';
  const showHandle = nameMode === 'full';
  return (
    <div
      className="relative w-full h-full"
      style={{
        background: spotlight
          ? rimColor
          : `linear-gradient(180deg, ${rimColor}, ${rimColor}55)`,
        clipPath:   SHIELD_CLIP,
      }}
    >
      <div
        className="absolute"
        style={{
          inset:      '1.5px',
          clipPath:   SHIELD_CLIP,
          background: 'linear-gradient(180deg, #0E0E0E 0%, #1A1A1A 100%)',
        }}
      >
        {/* Avatar — fills 70% of the shield, face-aligned */}
        <div className="absolute inset-x-0 top-0 h-[72%] overflow-hidden">
          <img
            src={avatar}
            alt={name}
            className="w-full h-full"
            style={{
              clipPath:       SHIELD_CLIP,
              transform:      'scale(1.02)',
              objectFit:      'cover',
              objectPosition: 'center top',
            }}
            onError={(e) => { e.target.src = dicebearFor(seed); }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(15,15,15,0.95) 100%)',
            }}
          />
          {/* Spotlight gets the MATCHED / REJECTED label; holding-zone
              cards stay clean per the redesign — no badges on FRONT. */}
          {spotlight && (
            <span
              className="absolute top-1 right-1 px-1.5 py-px rounded text-[7px] font-black uppercase tracking-wider"
              style={{ background: rimColor, color: '#0A0A0A' }}
            >
              {outcomeLabel}
            </span>
          )}
          {spotlight && platform && (
            <span className="absolute top-1 left-1 px-1 py-px rounded text-[6.5px] font-black uppercase tracking-wider bg-black/70 text-white">
              {platform}
            </span>
          )}
        </div>

        {/* Identity — name + handle, centered in the tapering bottom.
            `nameMode` lets the radial network suppress labels on dense
            rings so neighbouring shields don't collide. */}
        {showName && (
          <div className="absolute inset-x-0 top-[72%] bottom-0 px-1 flex flex-col items-center justify-center text-center">
            <p
              className="text-[10px] font-bold text-white truncate w-full leading-tight"
              data-testid={`card-name-${handle}`}
            >
              {name}
            </p>
            {showHandle && (
              <p
                className="text-[8px] truncate w-full"
                style={{ color: TEAL }}
                data-testid={`card-handle-${handle}`}
              >
                {handle}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── BACK — full individual context ────────────────────────────
function ShieldBackFace({
  creator, matchPct, uniqueR, ovrTag, ovrColor, platform,
}) {
  return (
    <div
      className="relative w-full h-full"
      style={{
        background: `linear-gradient(180deg, ${PINK}, ${PINK}55)`,
        clipPath:   SHIELD_CLIP,
      }}
    >
      <div
        className="absolute"
        style={{
          inset:      '1.5px',
          clipPath:   SHIELD_CLIP,
          background: 'linear-gradient(180deg, #0E0E0E 0%, #1A1A1A 100%)',
        }}
      >
        <div className="absolute inset-0 p-1.5 flex flex-col">
          {/* Cohort metrics — the primary content on the back */}
          <div className="flex items-center justify-between mb-1">
            <span
              className="text-[7px] uppercase tracking-wider font-black"
              style={{ color: PINK }}
            >
              {matchPct}% MATCH
            </span>
            <span
              className="flex items-center gap-0.5"
              data-testid={`card-overlap-${creator.handle}`}
              data-overlap-tag={ovrTag}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{
                  background: ovrColor,
                  boxShadow:  ovrTag === 'low' ? `0 0 4px ${ovrColor}aa` : 'none',
                }}
              />
              <span className="text-[6.5px] uppercase tracking-wider font-bold" style={{ color: ovrColor }}>
                {ovrTag}
              </span>
            </span>
          </div>

          <div className="text-center my-0.5" data-testid={`card-unique-reach-${creator.handle}`}>
            <p className="text-[14px] font-black leading-none" style={{ color: TEAL }}>
              +{formatReach(uniqueR)}
            </p>
            <p className="text-[6px] uppercase tracking-[0.15em] font-bold text-gray-500 mt-0.5">
              new reach
            </p>
          </div>

          {/* Identity + meta */}
          <div className="mt-1.5 space-y-0.5 text-[7px] text-gray-300">
            <p className="font-bold text-white truncate" data-testid={`card-back-name-${creator.handle}`}>
              {creator.name || creator.handle}
            </p>
            <p data-testid={`card-back-followers-${creator.handle}`}>
              {formatReach(creator.followers)} · {((creator.engagement_rate || 0) * 100).toFixed(1)}% eng
            </p>
            {platform && (
              <p className="truncate">on {creator.platforms.slice(0, 2).join(' · ')}</p>
            )}
            {creator.niches?.length ? (
              <p className="truncate flex items-center gap-0.5">
                <Sparkles className="h-2 w-2 flex-shrink-0" style={{ color: TEAL }} />
                <span className="truncate capitalize">{creator.niches[0]}</span>
              </p>
            ) : null}
            {creator.matched_dims?.length ? (
              <p
                className="truncate flex items-center gap-0.5"
                data-testid={`card-back-matches-${creator.handle}`}
              >
                <GitMerge className="h-2 w-2 flex-shrink-0" style={{ color: PINK }} />
                {creator.matched_dims.length} dim{creator.matched_dims.length === 1 ? '' : 's'} matched
              </p>
            ) : null}
          </div>

          <a
            href={`/discover?creator=${encodeURIComponent(creator.handle)}`}
            data-testid={`card-back-link-${creator.handle}`}
            onClick={(e) => e.stopPropagation()}
            className="mt-auto text-[6.5px] uppercase tracking-wider font-bold flex items-center justify-center gap-0.5 hover:text-white transition-colors pb-3"
            style={{ color: TEAL }}
          >
            View full <ExternalLink className="h-2 w-2" />
          </a>
        </div>
      </div>
    </div>
  );
}
